<template>
  <div class="auth-form">

    <!-- ─────────── MOBILE HEADER (small screens only) ─────────── -->
    <div class="auth-mhead d-md-none">
      <div class="auth-mhead__bg" aria-hidden="true"></div>
      <div class="auth-mhead__flourish" aria-hidden="true"></div>
      <div class="auth-mhead__content">
        <v-img :src="require('@/assets/logo.svg')" contain max-height="56" max-width="160"
          class="mx-auto auth-mhead__logo" />
        <div class="auth-mhead__title-row">
          <span class="auth-mhead__title">מערכת משווקים</span>
          <span class="auth-mhead__dot">·</span>
          <span class="auth-mhead__subtitle">סלולר כשר</span>
        </div>
      </div>
    </div>

    <!-- ─────────── AUTH CARD ─────────── -->
    <v-card class="auth-card" flat>
      <v-slide-x-transition mode="out-in">

        <!-- ====== STATE: LOGIN ====== -->
        <div v-if="!codeSent" key="login-state" class="auth-state">

          <header class="auth-state__head">
            <div class="auth-state__icon" aria-hidden="true">
              <v-icon color="#0d2c6d" size="22">mdi-lock-outline</v-icon>
            </div>
            <h1 class="auth-state__title">התחברות למערכת</h1>
            <p class="auth-state__subtitle">הזן את פרטי ההתחברות שלך כדי להמשיך</p>
          </header>

          <v-form ref="form" @submit.prevent="login" class="auth-state__form">

            <v-text-field v-model="email" label="אימייל" type="email"
              :rules="[v => !!v || 'שדה חובה', v => /.+@.+\..+/.test(v) || 'אימייל לא תקין']" outlined dense
              color="#0d2c6d" prepend-inner-icon="mdi-email-outline" class="auth-field" autocomplete="email" dir="rtl"
              @keyup.enter="focusPassword" ref="emailField"></v-text-field>

            <v-text-field v-model="password" label="סיסמה" :type="showPassword ? 'text' : 'password'"
              :rules="[v => !!v || 'שדה חובה']" outlined dense color="#0d2c6d" prepend-inner-icon="mdi-lock-outline"
              :append-icon="showPassword ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
              @click:append="showPassword = !showPassword" class="auth-field" autocomplete="current-password" dir="rtl"
              ref="passwordField"></v-text-field>

            <div class="auth-options">
              <v-checkbox v-model="rememberMe" label="זכור אותי" color="#0d2c6d" hide-details dense
                class="auth-remember"></v-checkbox>
              <button type="button" class="auth-link" @click="forgotPassword" :disabled="progressShow">
                <v-icon size="14">mdi-lock-reset</v-icon>
                <span>שכחתי סיסמה</span>
              </button>
            </div>

            <button type="submit" class="auth-submit" :disabled="progressShow">
              <v-progress-circular v-if="progressShow" indeterminate size="18" width="2.4" color="#fff" />
              <template v-else>
                <v-icon size="18" color="#fff">mdi-login-variant</v-icon>
                <span>התחברות</span>
              </template>
            </button>

            <div class="auth-support">
              <span class="auth-support__text">נתקלת בבעיה?</span>
              <a href="tel:+97223722723" class="auth-support__link">
                <v-icon size="14">mdi-phone-outline</v-icon>
                <span>חייג לתמיכה</span>
              </a>
            </div>
          </v-form>

        </div>

        <!-- ====== STATE: VERIFY ====== -->
        <div v-else key="verify-state" class="auth-state">

          <header class="auth-state__head">
            <div class="auth-state__icon auth-state__icon--verify" aria-hidden="true">
              <v-icon color="#fdb721" size="24">mdi-email-check-outline</v-icon>
            </div>
            <h1 class="auth-state__title">אימות כניסה</h1>
            <p class="auth-state__subtitle">קוד אימות נשלח אל</p>
            <div class="auth-verify-email">{{ maskEmail(email) }}</div>
          </header>

          <v-form ref="form" @submit.prevent="emailVerify" class="auth-state__form">

            <v-otp-input v-model="emailCode" length="6" type="number" class="auth-otp" @finish="emailVerify"
              ref="otpInput"></v-otp-input>

            <div v-if="countdown > 0" class="auth-countdown">
              <v-icon size="14" color="#94a3b8">mdi-clock-outline</v-icon>
              <span>ניתן לבקש קוד חדש בעוד <strong>{{ countdown }}</strong> שניות</span>
            </div>

            <button type="submit" class="auth-submit" :disabled="progressShow || emailCode.length < 6">
              <v-progress-circular v-if="progressShow" indeterminate size="18" width="2.4" color="#fff" />
              <template v-else>
                <v-icon size="18" color="#fff">mdi-check-circle-outline</v-icon>
                <span>אמת והתחבר</span>
              </template>
            </button>

            <div class="auth-verify-actions">
              <button type="button" class="auth-link" @click="resendCode" :disabled="countdown > 0">
                <v-icon size="14">mdi-refresh</v-icon>
                <span>שלח קוד חדש</span>
              </button>
              <button type="button" class="auth-link auth-link--back" @click="codeSent = false">
                <v-icon size="14">mdi-arrow-right</v-icon>
                <span>חזרה לטופס התחברות</span>
              </button>
            </div>
          </v-form>

        </div>

      </v-slide-x-transition>
    </v-card>

    <!-- ─────────── SNACKBAR ─────────── -->
    <v-snackbar v-model="snackbar" :color="snackbarColorBt" :timeout="4000" bottom right elevation="8" rounded="pill"
      class="auth-snackbar">
      <v-icon left class="mr-2">
        {{ snackbarColorBt === 'green' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
      </v-icon>
      {{ snacbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">סגור</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import ApiServices from '@/services/api.service'
import Auth from '@/services/auth.service.js'

export default {
  name: 'FormLogin',

  data: () => ({
    isResponsive: false,
    email: "",
    password: "",
    emailCode: "",
    progressShow: false,
    snackbar: false,
    snackbarColorBt: "green",
    snacbarText: "",
    codeSent: false,
    showPassword: false,
    rememberMe: false,
    countdown: 0,
    countdownInterval: null
  }),

  methods: {
    async login() {
      if (this.$refs.form.validate()) {
        this.progressShow = true
        let MyJSON = JSON.stringify({
          email: this.email,
          password: this.password,
          rememberMe: this.rememberMe
        });
        let api = process.env.VUE_APP_BASE_URL + "/auth/login"

        try {
          const response = await fetch(api, ApiServices.requestOptions("POST", MyJSON, ""));
          this.progressShow = false;
          const jsonObject = await response.json();
          if (response.status >= 400) {
            this.showSnackBar("שגיאת התחברות: " + jsonObject.message, "red");
          } else if (response.status === 200) {
            if (jsonObject.status === 1) {
              this.showSnackBar("התחברת בהצלחה!", "green");
              localStorage.setItem('token', jsonObject.token);
              let role = Auth.getUserRole();
              this.$router.replace({ name: "home" })
              return;
            }
            this.showSnackBar("אימייל לאימות נשלח בהצלחה!", "green");
            this.codeSent = true;
            this.startCountdown();

            // Focus on OTP input after a short delay
            setTimeout(() => {
              if (this.$refs.otpInput) {
                this.$refs.otpInput.focus();
              }
            }, 500);
          }
        } catch (error) {
          this.progressShow = false;
          this.showSnackBar("Error Login: " + error, "red");
        }
      }
    },

    async emailVerify() {
      if (this.emailCode.length === 6) {
        this.progressShow = true
        let MyJSON = JSON.stringify({ email: this.email, emailCode: this.emailCode });
        let api = process.env.VUE_APP_BASE_URL + "/auth/verify_code"

        try {
          const response = await fetch(api, ApiServices.requestOptions("POST", MyJSON, ""));
          this.progressShow = false;
          const jsonObject = await response.json();
          if (response.status >= 400) {
            this.showSnackBar("שגיאת התחברות: " + jsonObject.message, "red");
          } else if (response.status === 200) {
            this.showSnackBar("התחברת בהצלחה!", "green");
            localStorage.setItem('token', jsonObject.token);
            let role = Auth.getUserRole();
            this.$router.replace({ name: "home" })
          }
        } catch (error) {
          this.progressShow = false;
          this.showSnackBar("Error Login: " + error, "red");
        }
      }
    },

    validateNumericInput(event) {
      const charCode = event.charCode;
      if (charCode < 48 || charCode > 57) {
        event.preventDefault();
      }
    },

    showSnackBar(message, color) {
      this.snacbarText = message;
      this.snackbarColorBt = color;
      this.snackbar = true
    },

    async forgotPassword() {
      if (this.email === "") {
        this.showSnackBar("יש להזין אימייל לצורך איפוס הסיסמה", "red");
        return
      }
      this.progressShow = true;
      let MyJSON = JSON.stringify({ email: this.email });
      let api = process.env.VUE_APP_BASE_URL + "/auth/forgotPassword"

      try {
        const response = await fetch(api, ApiServices.requestOptions("POST", MyJSON, ""));
        this.progressShow = false;
        if (response.status === 401) {
          this.showSnackBar("שגיאה: אימייל לא קיים", "red");
        } else if (response.status === 500) {
          this.showSnackBar("שגיאת שרת", "red");
        } else if (response.status === 200) {
          const jsonObject = await response.json();
          if (jsonObject.emailSent) {
            this.showSnackBar("נשלח מייל לאיפוס הסיסמה", "green");
          } else {
            this.showSnackBar("שגיאה בשליחת מייל איפוס", "red");
          }
        }
      } catch (error) {
        this.progressShow = false;
        this.showSnackBar("Error Login: " + error, "red");
      }
    },

    resendCode() {
      if (this.countdown > 0) return;

      // Implement resend code functionality
      this.showSnackBar("קוד חדש נשלח לאימייל שלך", "green");
      this.startCountdown();
    },

    startCountdown() {
      this.countdown = 60; // 60 seconds countdown
      clearInterval(this.countdownInterval);
      this.countdownInterval = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          clearInterval(this.countdownInterval);
        }
      }, 1000);
    },

    maskEmail(email) {
      if (!email) return '';
      const parts = email.split('@');
      if (parts.length !== 2) return email;

      const name = parts[0];
      const domain = parts[1];

      let maskedName = '';
      if (name.length <= 2) {
        maskedName = name[0] + '*';
      } else {
        maskedName = name[0] + '*'.repeat(name.length - 2) + name[name.length - 1];
      }

      return maskedName + '@' + domain;
    },

    focusPassword() {
      if (this.$refs.passwordField) {
        this.$refs.passwordField.focus();
      }
    },

    handleResize() {
      this.isResponsive = window.innerWidth <= 500;
    },

    handleEnterKeyOtp(event) {
      if (event.keyCode === 13) {
        this.emailVerify();
      }
    },
  },

  watch: {
    $route() {
      document.title = 'התחברות | מערכת משווקים - סלולר כשר'
    },
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    clearInterval(this.countdownInterval);
  },

  mounted() {
    document.title = 'התחברות | מערכת משווקים - סלולר כשר'
    this.isResponsive = window.innerWidth <= 500;
    window.addEventListener('resize', this.handleResize);

    // Auto-focus email field on mount
    setTimeout(() => {
      if (this.$refs.emailField) {
        this.$refs.emailField.focus();
      }
    }, 500);
  },
}
</script>

<style scoped>
/* ─────────────── BASE ─────────────── */
.auth-form {
  --c-navy: #0d2c6d;
  --c-navy-soft: #142f78;
  --c-navy-deep: #081f54;
  --c-indigo: #3F51B5;
  --c-purple: #8066f0;
  --c-gold: #fdb721;
  --c-bg: #f3f6f9;
  --c-surface: #ffffff;
  --c-border: #e3e8ef;
  --c-border-soft: #eef2f7;
  --c-text: #0f172a;
  --c-text-soft: #475569;
  --c-text-mute: #94a3b8;
  --c-ease: cubic-bezier(0.22, 1, 0.36, 1);
  --c-shadow: 0 22px 50px -16px rgba(13, 44, 109, 0.18), 0 8px 16px -8px rgba(13, 44, 109, 0.08);

  width: 100%;
  max-width: 460px;
  font-family: 'Assistant', 'Heebo', system-ui, sans-serif;
}

/* ─────────────── MOBILE HEADER ─────────────── */
.auth-mhead {
  position: relative;
  padding: 28px 24px 36px;
  margin-bottom: -16px;
  overflow: hidden;
  border-radius: 0 0 22px 22px;
  isolation: isolate;
}

.auth-mhead__bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #0a2055 0%, #142f78 55%, #1c3d95 100%);
  z-index: -2;
}

.auth-mhead__flourish {
  position: absolute;
  top: -60px;
  left: -60px;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(253, 183, 33, 0.20) 0%, transparent 70%);
  filter: blur(40px);
  z-index: -1;
}

.auth-mhead__content {
  text-align: center;
}

.auth-mhead__logo {
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.18));
}

.auth-mhead__title-row {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.auth-mhead__title {
  color: #fff;
  font-weight: 800;
  font-size: 1.05rem;
  letter-spacing: -0.01em;
}

.auth-mhead__dot {
  color: var(--c-gold);
}

.auth-mhead__subtitle {
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.88rem;
  font-weight: 600;
}

/* ─────────────── CARD ─────────────── */
.auth-card {
  background: var(--c-surface) !important;
  border-radius: 18px !important;
  border: 1px solid var(--c-border-soft);
  box-shadow: var(--c-shadow) !important;
  overflow: hidden;
  position: relative;
}

/* Top accent strip — navy → gold gradient */
.auth-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--c-navy) 0%, var(--c-navy-soft) 50%, var(--c-gold) 100%);
  z-index: 1;
}

/* ─────────────── STATE WRAPPER ─────────────── */
.auth-state {
  padding: 30px 32px 28px;
  animation: auth-fadeIn 0.4s var(--c-ease);
}

@keyframes auth-fadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ── State head ── */
.auth-state__head {
  text-align: center;
  margin-bottom: 24px;
}

.auth-state__icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 14px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: rgba(13, 44, 109, 0.08);
  border: 1px solid rgba(13, 44, 109, 0.14);
}

.auth-state__icon--verify {
  background: rgba(253, 183, 33, 0.12);
  border-color: rgba(253, 183, 33, 0.28);
}

.auth-state__title {
  margin: 0 0 6px;
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--c-navy);
  letter-spacing: -0.015em;
}

.auth-state__subtitle {
  margin: 0;
  color: var(--c-text-soft);
  font-size: 0.92rem;
  line-height: 1.5;
}

.auth-verify-email {
  display: inline-block;
  margin-top: 10px;
  padding: 6px 16px;
  border-radius: 999px;
  background: rgba(13, 44, 109, 0.06);
  border: 1px solid rgba(13, 44, 109, 0.10);
  color: var(--c-navy);
  font-weight: 700;
  font-size: 0.92rem;
  direction: ltr;
}

/* ─────────────── FIELDS ─────────────── */
.auth-state__form {}

.auth-field {
  margin-bottom: 6px;
}

::v-deep .auth-field .v-input__slot {
  border-radius: 11px !important;
  background: #fbfcfe !important;
}

::v-deep .auth-field fieldset {
  border-color: var(--c-border) !important;
  transition: all 0.2s var(--c-ease);
}

::v-deep .auth-field:hover fieldset {
  border-color: #c7d2e0 !important;
}

::v-deep .auth-field.v-input--is-focused fieldset {
  border-color: var(--c-navy) !important;
  border-width: 2px !important;
  box-shadow: 0 0 0 4px rgba(13, 44, 109, 0.08);
}

::v-deep .auth-field .v-label {
  font-family: inherit;
}

::v-deep .auth-field .v-input__prepend-inner {
  margin-top: 9px !important;
}

::v-deep .auth-field .v-input__prepend-inner .v-icon {
  color: var(--c-text-mute) !important;
}

::v-deep .auth-field.v-input--is-focused .v-input__prepend-inner .v-icon {
  color: var(--c-navy) !important;
}

::v-deep .auth-field .v-input__append-inner .v-icon {
  color: var(--c-text-mute) !important;
  transition: color 0.2s var(--c-ease);
}

::v-deep .auth-field .v-input__append-inner .v-icon:hover {
  color: var(--c-navy) !important;
}

/* ─────────────── OPTIONS ROW ─────────────── */
.auth-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 4px 0 18px;
}

.auth-remember {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

::v-deep .auth-remember .v-label {
  font-family: inherit;
  font-size: 0.88rem;
  color: var(--c-text-soft);
}

.auth-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 4px;
  border: none;
  background: transparent;
  color: var(--c-navy);
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s var(--c-ease);
}

.auth-link ::v-deep .v-icon {
  color: inherit !important;
}

.auth-link:hover:not(:disabled) {
  color: var(--c-gold);
  transform: translateX(-2px);
}

.auth-link:disabled {
  color: var(--c-text-mute);
  cursor: not-allowed;
}

/* ─────────────── SUBMIT BUTTON ─────────────── */
.auth-submit {
  width: 100%;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--c-navy) 0%, var(--c-navy-soft) 100%);
  color: #fff;
  font-family: inherit;
  font-weight: 800;
  font-size: 1rem;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: all 0.25s var(--c-ease);
  box-shadow: 0 10px 22px -8px rgba(13, 44, 109, 0.5);
  position: relative;
  overflow: hidden;
}

/* Subtle shine effect on hover */
.auth-submit::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.20), transparent);
  transition: left 0.6s var(--c-ease);
}

.auth-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 14px 26px -8px rgba(13, 44, 109, 0.55);
  filter: brightness(1.06);
}

.auth-submit:hover:not(:disabled)::after {
  left: 100%;
}

.auth-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
}

.auth-submit ::v-deep .v-icon {
  color: #fff !important;
}

/* ─────────────── SUPPORT FOOTER ─────────────── */
.auth-support {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px dashed var(--c-border);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

.auth-support__text {
  font-size: 0.85rem;
  color: var(--c-text-mute);
}

.auth-support__link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--c-navy);
  text-decoration: none;
  font-size: 0.86rem;
  font-weight: 700;
  transition: color 0.2s var(--c-ease);
}

.auth-support__link:hover {
  color: var(--c-gold);
}

.auth-support__link ::v-deep .v-icon {
  color: inherit !important;
}

/* ─────────────── OTP ─────────────── */
.auth-otp {
  margin: 6px 0 14px;
}

::v-deep .auth-otp .v-input__slot {
  justify-content: center;
}

::v-deep .auth-otp input {
  width: 44px !important;
  height: 52px !important;
  margin: 0 5px !important;
  font-size: 1.45rem !important;
  font-weight: 700 !important;
  font-family: 'Assistant', 'Heebo', system-ui, sans-serif !important;
  color: var(--c-navy) !important;
  border: 1.5px solid var(--c-border) !important;
  border-radius: 10px !important;
  background: #fbfcfe !important;
  transition: all 0.2s var(--c-ease);
}

::v-deep .auth-otp input:hover {
  border-color: #c7d2e0 !important;
}

::v-deep .auth-otp input:focus {
  border-color: var(--c-navy) !important;
  border-width: 2px !important;
  background: #fff !important;
  box-shadow: 0 0 0 4px rgba(13, 44, 109, 0.10) !important;
  transform: translateY(-2px);
}

/* ─────────────── COUNTDOWN ─────────────── */
.auth-countdown {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: 0 auto 18px;
  padding: 7px 14px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.10);
  border: 1px solid rgba(148, 163, 184, 0.22);
  color: var(--c-text-soft);
  font-size: 0.82rem;
  font-weight: 600;
  width: fit-content;
}

.auth-countdown {
  display: flex;
  justify-content: center;
  margin: 0 0 18px;
}

.auth-countdown strong {
  color: var(--c-navy);
  font-weight: 800;
  font-feature-settings: "tnum" 1;
  margin: 0 3px;
}

.auth-countdown ::v-deep .v-icon {
  color: var(--c-text-mute) !important;
}

/* ─────────────── VERIFY ACTIONS ─────────────── */
.auth-verify-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px dashed var(--c-border);
}

.auth-link--back {
  color: var(--c-text-soft);
}

.auth-link--back:hover:not(:disabled) {
  color: var(--c-navy);
}

/* ─────────────── SNACKBAR ─────────────── */
::v-deep .auth-snackbar .v-snack__wrapper {
  font-family: 'Assistant', 'Heebo', system-ui, sans-serif !important;
  font-weight: 600;
}

/* ─────────────── RESPONSIVE ─────────────── */
@media (max-width: 960px) {
  .auth-form {
    max-width: 100%;
  }
}

@media (max-width: 600px) {
  .auth-card {
    border-radius: 22px 22px 0 0 !important;
  }

  .auth-state {
    padding: 26px 22px 24px;
  }

  .auth-state__title {
    font-size: 1.3rem;
  }

  ::v-deep .auth-otp input {
    width: 40px !important;
    height: 48px !important;
    font-size: 1.3rem !important;
    margin: 0 3px !important;
  }

  .auth-verify-actions {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  }
}

@media (max-width: 380px) {
  ::v-deep .auth-otp input {
    width: 36px !important;
    height: 44px !important;
    font-size: 1.15rem !important;
    margin: 0 2px !important;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .auth-state {
    animation: none;
  }

  .auth-submit::after {
    display: none;
  }
}

/* iOS safe-area */
@supports (-webkit-touch-callout: none) {
  .auth-form {
    padding-bottom: env(safe-area-inset-bottom);
  }
}
</style>